import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Clock, MessageCircle } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';

export const ContactSection = ({ onBookClick }) => {
  const { t, isRTL } = useLanguage();

  const contactInfo = [
    {
      icon: MapPin,
      title: t.contact.address,
      value: t.contact.addressValue,
      link: 'https://maps.google.com/?q=26.4258774,50.1118835',
    },
    {
      icon: Phone,
      title: t.contact.phone,
      value: '+966 53 763 7775',
      link: 'tel:+966537637775',
    },
    {
      icon: Clock,
      title: t.contact.hours,
      value: t.contact.hoursValue,
      subValue: t.contact.closed,
    },
  ];

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="section-padding bg-white"
    >
      <div className="container mx-auto px-4 md:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-[#D4A39A] text-sm font-semibold uppercase tracking-widest">
            {t.contact.subtitle}
          </span>
          <h2 
            data-testid="contact-title"
            className="heading text-3xl md:text-4xl lg:text-5xl font-bold text-[#4A4A4A] mt-3"
          >
            {t.contact.title}
          </h2>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Map */}
          <motion.div
            initial={{ opacity: 0, x: isRTL ? 50 : -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="rounded-2xl overflow-hidden shadow-xl h-[400px] lg:h-full min-h-[400px]"
          >
            <iframe
              data-testid="google-map"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3578.1234567890123!2d50.1118835!3d26.4258774!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjbCsDI1JzMzLjIiTiA1MMKwMDYnNDIuOCJF!5e0!3m2!1sen!2ssa!4v1234567890123"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Sirena Beauty Salon Location"
            />
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: isRTL ? -50 : 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="flex flex-col justify-center"
          >
            <div className="space-y-8">
              {contactInfo.map((info, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                  className="flex items-start gap-4"
                >
                  <div className="w-14 h-14 rounded-full bg-[#F9F7F2] flex items-center justify-center flex-shrink-0">
                    <info.icon className="w-6 h-6 text-[#D4A39A]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#4A4A4A] text-lg mb-1">
                      {info.title}
                    </h3>
                    {info.link ? (
                      <a
                        href={info.link}
                        target={info.link.startsWith('https') ? '_blank' : undefined}
                        rel={info.link.startsWith('https') ? 'noopener noreferrer' : undefined}
                        className="text-[#7D7D7D] hover:text-[#D4A39A] transition-colors"
                      >
                        {info.value}
                      </a>
                    ) : (
                      <>
                        <p className="text-[#7D7D7D]">{info.value}</p>
                        {info.subValue && (
                          <p className="text-[#D4A39A] font-medium text-sm mt-1">
                            {info.subValue}
                          </p>
                        )}
                      </>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.6 }}
              className="flex flex-col sm:flex-row gap-4 mt-10"
            >
              <Button
                data-testid="contact-book-btn"
                onClick={onBookClick}
                className="btn-primary flex-1"
              >
                {t.hero.bookAppointment}
              </Button>
              <Button
                data-testid="contact-whatsapp-btn"
                asChild
                className="flex-1 bg-[#25D366] hover:bg-[#20BD5A] text-white rounded-full"
              >
                <a
                  href="https://wa.me/966537637775"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2"
                >
                  <MessageCircle className="w-5 h-5" />
                  {t.contact.whatsapp}
                </a>
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
